import React, { useState, useEffect } from 'react';
import { EditPage } from '../../../components/crud/EditPage';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import { Spinner } from '../../../components/ui/spinner';
import { useToast } from '../../../components/ui/use-toast';
import { useShow, useUpdate, useSelect } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';

const CAPABILITIES = ['hl7', 'astm', 'serial', 'tcp', 'dicom'];

export const ConnectorDeviceEdit: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { query } = useShow({ resource: 'devices/connectors' });
  const { mutate, isLoading: saving } = useUpdate();
  const { options: branchOptions } = useSelect({ resource: 'branches', optionLabel: 'name', optionValue: 'id' });
  const record = query?.data?.data;

  const [values, setValues] = useState({
    name: '', device_type: '', serial_number: '', mac_address: '', branch: '',
    location_description: '', ip_address: '', port_range_start: '', port_range_end: '',
    vpn_enabled: false, firewall_rules: '', security_level: 'standard',
    firmware_version: '', status: 'active', health_score: '', deployment_notes: '',
    capabilities: [] as string[],
    dicom_enabled: false, dicom_ae_title: '', dicom_port: '11112',
  });

  const set = (f: string, v: any) => setValues(prev => ({ ...prev, [f]: v }));
  const toggleCap = (c: string) =>
    setValues(prev => ({
      ...prev,
      capabilities: prev.capabilities.includes(c)
        ? prev.capabilities.filter(x => x !== c)
        : [...prev.capabilities, c],
    }));

  useEffect(() => {
    if (record) {
      const fw = Array.isArray(record.firewall_rules) ? record.firewall_rules.join('\n') : (record.firewall_rules || '');
      setValues({
        name: record.name || '', device_type: record.device_type || '', serial_number: record.serial_number || '',
        mac_address: record.mac_address || '', branch: record.branch ? String(record.branch) : '',
        location_description: record.location_description || '',
        ip_address: record.ip_address || '', port_range_start: record.port_range_start?.toString() || '',
        port_range_end: record.port_range_end?.toString() || '', vpn_enabled: !!record.vpn_enabled,
        firewall_rules: fw, security_level: record.security_level || 'standard',
        firmware_version: record.firmware_version || '', status: record.status || 'active',
        health_score: record.health_score?.toString() || '', deployment_notes: record.deployment_notes || '',
        capabilities: Array.isArray(record.capabilities) ? record.capabilities : [],
        dicom_enabled: !!record.dicom_enabled, dicom_ae_title: record.dicom_ae_title || '',
        dicom_port: record.dicom_port?.toString() || '11112',
      });
    }
  }, [record]);

  if (query?.isLoading) return <div className="flex justify-center p-12"><Spinner /></div>;

  const handleSave = () => {
    mutate({ resource: 'devices/connectors', id: record?.id, values: {
      name: values.name,
      device_type: values.device_type,
      serial_number: values.serial_number,
      mac_address: values.mac_address,
      branch: values.branch || undefined,
      location_description: values.location_description,
      ip_address: values.ip_address || undefined,
      port_range_start: values.port_range_start ? Number(values.port_range_start) : undefined,
      port_range_end: values.port_range_end ? Number(values.port_range_end) : undefined,
      vpn_enabled: values.vpn_enabled,
      firewall_rules: values.firewall_rules ? values.firewall_rules.split('\n').map(s => s.trim()).filter(Boolean) : [],
      security_level: values.security_level,
      firmware_version: values.firmware_version,
      status: values.status,
      health_score: values.health_score ? Number(values.health_score) : undefined,
      deployment_notes: values.deployment_notes,
      capabilities: values.capabilities,
      dicom_enabled: values.dicom_enabled,
      dicom_ae_title: values.dicom_ae_title,
      dicom_port: values.dicom_port ? Number(values.dicom_port) : 11112,
    }}, {
      onSuccess: () => { toast({ title: 'Device updated' }); navigate('/devices/connectors'); },
      onError: (e: any) => toast({ variant: 'destructive', title: 'Failed to update device', description: e?.message }),
    });
  };

  return (
    <EditPage title="Edit Connector Device" footerButtons={<Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>}>
      <div className="space-y-6">
        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Device Name <span className="text-destructive">*</span></Label><Input value={values.name} onChange={e => set('name', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Device Type <span className="text-destructive">*</span></Label>
              <Select value={values.device_type} onValueChange={v => set('device_type', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pc">Connector PC</SelectItem><SelectItem value="minipc">Mini PC</SelectItem><SelectItem value="rpi">Raspberry Pi</SelectItem><SelectItem value="embedded">Embedded System</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Serial Number</Label><Input value={values.serial_number} onChange={e => set('serial_number', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>MAC Address</Label><Input value={values.mac_address} onChange={e => set('mac_address', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Branch</Label>
              <Select value={values.branch} onValueChange={v => set('branch', v)}><SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{(branchOptions || []).map((o: any) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
        </CardContent></Card>

        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Location & Network</h4>
          <div className="space-y-1.5"><Label>Location Description</Label><Textarea rows={2} value={values.location_description} onChange={e => set('location_description', e.target.value)} /></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>IP Address</Label><Input value={values.ip_address} onChange={e => set('ip_address', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Port Start</Label><Input type="number" value={values.port_range_start} onChange={e => set('port_range_start', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Port End</Label><Input type="number" value={values.port_range_end} onChange={e => set('port_range_end', e.target.value)} /></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={values.vpn_enabled} onCheckedChange={v => set('vpn_enabled', v)} /><Label>VPN Enabled</Label></div>
          <div className="space-y-1.5"><Label>Firewall Rules (one per line)</Label><Textarea rows={3} value={values.firewall_rules} onChange={e => set('firewall_rules', e.target.value)} /></div>
        </CardContent></Card>

        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Capabilities</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {CAPABILITIES.map(c => (
              <div key={c} className="flex items-center gap-2">
                <Switch checked={values.capabilities.includes(c)} onCheckedChange={() => toggleCap(c)} />
                <Label className="uppercase">{c}</Label>
              </div>
            ))}
          </div>
        </CardContent></Card>

        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">DICOM SCP (PACS Receiver)</h4>
          <div className="flex items-center gap-2"><Switch checked={values.dicom_enabled} onCheckedChange={v => set('dicom_enabled', v)} /><Label>Run DICOM C-STORE/C-ECHO SCP on this connector</Label></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>SCP AE Title</Label><Input maxLength={16} value={values.dicom_ae_title} onChange={e => set('dicom_ae_title', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>SCP Port</Label><Input type="number" min={1} max={65535} value={values.dicom_port} onChange={e => set('dicom_port', e.target.value)} /></div>
          </div>
        </CardContent></Card>

        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Security & Status</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Security Level</Label>
              <Select value={values.security_level} onValueChange={v => set('security_level', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="basic">Basic</SelectItem><SelectItem value="standard">Standard</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={values.status} onValueChange={v => set('status', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem><SelectItem value="error">Error</SelectItem><SelectItem value="offline">Offline</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Health Score (0-100)</Label><Input type="number" min={0} max={100} value={values.health_score} onChange={e => set('health_score', e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Firmware Version</Label><Input value={values.firmware_version} onChange={e => set('firmware_version', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Deployment Notes</Label><Textarea rows={2} value={values.deployment_notes} onChange={e => set('deployment_notes', e.target.value)} /></div>
        </CardContent></Card>
      </div>
    </EditPage>
  );
};

export default ConnectorDeviceEdit;
